import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { heroMovies } from '@/mocks/movies';

export default function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  useEffect(() => {
    const timer = setInterval(() => {
      goTo((current + 1) % heroMovies.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [current, goTo]);

  const movie = heroMovies[current];

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center transition-all duration-700 ease-in-out"
        style={{ backgroundImage: `url(${movie.coverImage})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70" />

      <div className="absolute inset-0 flex items-center">
        <div className="px-4 md:px-6 w-full">
          <div className="max-w-7xl mx-auto">
            <div className={`transition-all duration-500 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
              <div className="flex flex-wrap items-center gap-2 mb-3 md:mb-4">
                <span className="px-2.5 py-1 text-xs font-medium bg-accent-500/90 text-background-50 rounded-full whitespace-nowrap">
                  {movie.quality}
                </span>
                <span className="px-2.5 py-1 text-xs font-medium bg-background-50/20 text-background-50 rounded-full whitespace-nowrap">
                  {movie.year}
                </span>
                <span className="px-2.5 py-1 text-xs font-medium bg-background-50/20 text-background-50 rounded-full whitespace-nowrap">
                  {movie.rating} 分
                </span>
              </div>

              <h2 className="text-2xl md:text-5xl font-heading font-bold text-background-50 mb-2 md:mb-3 leading-tight">
                {movie.title}
              </h2>
              <p className="text-sm md:text-base text-background-200 max-w-lg mb-3 md:mb-5 leading-relaxed hidden sm:block">
                {movie.description}
              </p>

              <div className="flex items-center gap-3 flex-wrap">
                <Link
                  to={`/article/${movie.id}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 md:px-6 md:py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm md:text-base whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                >
                  <i className="ri-play-fill text-lg"></i>
                  立即观看
                </Link>
                <Link
                  to={`/article/${movie.id}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 md:px-6 md:py-3 bg-background-50/15 backdrop-blur-sm text-background-50 rounded-full font-medium text-sm md:text-base whitespace-nowrap cursor-pointer hover:bg-background-50/25 transition-colors"
                >
                  <i className="ri-information-line text-lg"></i>
                  详情介绍
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {heroMovies.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goTo(idx)}
            className={`w-2.5 h-2.5 rounded-full cursor-pointer transition-all duration-300 ${
              idx === current ? 'bg-accent-500 w-7' : 'bg-background-50/40 hover:bg-background-50/60'
            }`}
            aria-label={`第${idx + 1}张`}
          />
        ))}
      </div>

      <div className="absolute bottom-6 right-4 md:right-6 text-xs text-background-300">
        {current + 1} / {heroMovies.length}
      </div>
    </section>
  );
}